import Chip from '@carvana/ds-chip';
import cx from 'classnames';
import { Clock } from 'lucide-react';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Summary } from '|/app/services';
import { isNativeWebview } from '|/helpers/is-native-app';
import styles from './lock-timer.module.css';

const isMoreThanAYearOut = (date: string | Date | null): boolean => {
  if (date === null) return false;
  const inputDate = dayjs(date);
  const oneYearFromNow = dayjs().add(1, 'year');
  return inputDate.isAfter(oneYearFromNow);
};

const formatNumberAsTime = (number: number) => {
  return String(number).padStart(2, '0');
};

const getTimerFormat = (expiresOn: string | null) => {
  if (expiresOn === null) return '00:00:00';
  const now = dayjs();
  const expires = dayjs(expiresOn);

  if (!expires.isValid()) return '00:00:00';
  if (expires.isBefore(now)) return '00:00:00';

  const hours = formatNumberAsTime(expires.diff(now, 'hour'));
  const minutes = formatNumberAsTime(expires.diff(now, 'minute') % 60);
  const seconds = formatNumberAsTime(Math.floor(expires.diff(now, 'second') % 60));
  return `${hours}:${minutes}:${seconds}`;
};

interface LockTimerProps {
  orderSummary?: Summary | null;
  onClick?: () => void;
  /** Renders the icon-and-text timer at every viewport instead of the desktop chip. */
  plain?: boolean;
  /** Placement is the header's business — the timer only owns its own appearance. */
  className?: string;
}

const LockTimer: React.FC<LockTimerProps> = ({
  orderSummary = null,
  onClick,
  plain = false,
  className
}) => {
  const { vehicle: { unlockDateTime = null, isAvailable = false } = {} } = orderSummary ?? {};
  const isPaused = isMoreThanAYearOut(unlockDateTime);
  const [timerDisplay, setTimerDisplay] = useState(getTimerFormat(unlockDateTime));

  useEffect(() => {
    const id = setTimeout(() => {
      const newTimerDisplay = getTimerFormat(unlockDateTime);
      if (newTimerDisplay !== timerDisplay) setTimerDisplay(newTimerDisplay);
    }, 1000);

    return () => {
      clearTimeout(id);
    };
  }, [timerDisplay, unlockDateTime]);

  if (!isAvailable || isPaused) return null;

  const readout = (
    <span className={styles.timerText}>
      {timerDisplay.split('').map((char, index) => (
        <span key={index} className={styles.timerChar}>
          {char}
        </span>
      ))}
    </span>
  );

  if (plain) {
    return (
      <button
        className={cx(styles.plainTimer, className)}
        onClick={onClick}
        data-analytics-id="pp_header_lock_timer"
        type="button"
      >
        <Clock className={styles.timerIcon} size={16} color="currentColor" />
        {readout}
      </button>
    );
  }

  return (
    <div className={cx(styles.timerContainer, className)}>
      {/* Mobile timer - plain text with icon */}
      <button
        className={isNativeWebview() ? styles.mobileTimerTreatment : styles.mobileTimer}
        onClick={onClick}
        data-analytics-id="pp_header_lock_timer"
        type="button"
      >
        <Clock className={styles.timerIcon} size={16} color="currentColor" />
        {readout}
      </button>

      {/* Desktop timer - chip */}
      <Chip
        shape="pill"
        size="default"
        icon={<Clock size={16} color="currentColor" />}
        iconPosition="left"
        disabled={false}
        className={styles.desktopTimer}
        onClick={onClick}
        data-analytics-id="pp_header_lock_timer"
      >
        {readout}
      </Chip>
    </div>
  );
};

export default LockTimer;
